// GENERATED by `pnpm run gen:narrative` from src/core/content/narrative/rung-beats.md — DO NOT EDIT.
// Hand edits here are drift by definition: edit the markdown source and regenerate
// (the gen-narrative verify gate rebuilds this file and byte-compares it).
// Data only — the types + helpers stay hand-written in rungBeats.ts, which re-exports this.

import type { RankId } from './ranks';
import type { RungScene } from './rungBeats';
import { NPC_NAME } from './voices';

export const RUNG_BEATS: Partial<Record<RankId, RungScene>> = {
  R1: {
    id: 'rung-r1',
    rank: 'R1',
    voice: 'steward',
    speaker: 'genemon',
    motivates: [
      'room-gate',
      'room-paddies',
      'room-woodshed',
      'room-kitchen',
      'verb-farm',
      'verb-haul',
      'readout-clock',
      'readout-stamina',
      'panel-rung-ladder',
    ],
    greeting: [
      {
        voice: 'narrator',
        text: "Morning at the board. The hands take their rice at the threshold; the steward's papers hold the table's far end — an estate's worth of paper on a kitchen table. Genemon calls you up from the step without looking up.",
      },
      {
        voice: 'steward',
        speaker: NPC_NAME.genemon,
        text: '"Two hands took the lowland road this week. Wages drawn through the day they left, lines struck. The yard is short. The work is not."',
      },
      { voice: 'narrator', text: 'It is the first you have heard of any leaving.' },
      {
        voice: 'steward',
        speaker: NPC_NAME.genemon,
        text: '"Terms. Work, sun to sun, at what the yard wants doing — hired by the day, counted in the book. Meals at the threshold, morning and evening. The woodshed corner and a mat. No coin: the house\'s coin is spoken for before it reaches the yard. Objections are heard now or kept. The paddies want you by full light."',
      },
      {
        voice: 'narrator',
        text: "Nobody stops eating. Two hands lost was a morning's talk; one stray kept is not even that. The bowls go on.",
      },
      {
        voice: 'steward',
        speaker: NPC_NAME.genemon,
        text: '"The board has heard it. That serves for witness."',
      },
      {
        voice: 'narrator',
        text: 'He opens the day-book to a page already half-filled with other men.',
      },
      {
        voice: 'narrator',
        text: 'On the veranda above the step, Naoyuki has stopped — the second son, papers of his own under one arm. Passing, and made a witness like the rest of the room.',
      },
      { voice: 'official', speaker: NPC_NAME.naoyuki, text: '"The house is glad of —"' },
      {
        voice: 'narrator',
        text: 'The sentence stops. He stands with it a moment, as if he has read it back and found it not worth the ink.',
      },
      {
        voice: 'official',
        speaker: NPC_NAME.naoyuki,
        text: '"Two hands left. You were here. That is all it is."',
      },
      { voice: 'narrator', text: 'He goes on along the veranda. He looked at you once.' },
      {
        voice: 'narrator',
        text: 'Then Genemon takes up the brush, and asks it plainly, at board volume. The room hears without listening.',
      },
      {
        voice: 'steward',
        speaker: NPC_NAME.genemon,
        text: '"One thing before the ink. If a claim follows you here, I enter it against your board. Is anything owing on you?"',
      },
    ],
    topics: [
      {
        id: 'r1-the-two',
        label: '"The two that left?"',
        answer: [
          {
            voice: 'steward',
            speaker: NPC_NAME.genemon,
            text: '"Left owed nothing, owing nothing. Struck clean. The lowlands pay coin in winter; I could not argue the sum, so I did not argue it."',
          },
        ],
      },
      {
        id: 'r1-why-keep',
        label: '"Why keep me?"',
        answer: [
          {
            voice: 'steward',
            speaker: NPC_NAME.genemon,
            text: '"The paddies take six hands at the least. Five sleep here now. You are the sixth. If a better sixth comes by, the book will say so."',
          },
        ],
      },
      {
        id: 'r1-the-meals',
        label: '"Just the meals?"',
        answer: [
          {
            voice: 'steward',
            speaker: NPC_NAME.genemon,
            text: '"Six hands\' work, five men fed. The sixth eats. That is the whole of it."',
          },
        ],
      },
    ],
    decision: {
      prompt: 'Is anything owing on you?',
      options: [
        {
          id: 'r1-none-claimed',
          label: '"Not that I know."',
          say: '"Not that I know."',
          react: '"Then the book says none claimed. It has lived with worse."',
          flags: ['r1-none-claimed'],
        },
        {
          id: 'r1-kept',
          label: '"Say nothing."',
          say: '"…"',
          react: '"Kept, then. The book carries what is claimed, and I keep my own margins."',
          memory: [{ npc: 'genemon', warmthDelta: 1, regard: 'kept-accounts' }],
          flags: ['r1-kept-owing'],
        },
        {
          id: 'r1-and-if',
          label: '"And if there were?"',
          say: '"And if there were?"',
          react:
            '"Then the house\'s claim comes first, while you eat its rice. After that — whoever can find you. So far nobody has asked."',
          flags: ['r1-asked-owing'],
        },
      ],
    },
  },

  R3: {
    id: 'rung-r3',
    rank: 'R3',
    voice: 'arms',
    speaker: 'kihei',
    motivates: [
      'tab-combat',
      'panel-drill-yard',
      'readout-combat-level',
      'panel-bestiary',
      'room-kura',
      'room-weir-reeds',
      'room-sickroom',
    ],
    greeting: [
      {
        voice: 'narrator',
        text: 'Dawn comes grey through the kura door. The blood on the sill has dried black, and most of it is yours. Inside, the rice sits in its rows, untouched, exactly as it was put. Sōan has strapped your ribs so tight that breathing is a decision.',
      },
      {
        voice: 'narrator',
        text: 'Kihei crouches at the sill a long time, reading the blood the way Genemon reads a column of figures. The trail goes west, into the trees, in drops that get no smaller.',
      },
      { voice: 'arms', speaker: NPC_NAME.kihei, text: '"…Alive. Good."' },
      {
        voice: 'narrator',
        text: 'For a moment there is more in him than the verdict. He checks the broken bar instead.',
      },
      {
        voice: 'narrator',
        text: 'Genemon stands in the doorway. Whatever he came to say, he writes something in the day-book first.',
      },
      {
        voice: 'steward',
        speaker: NPC_NAME.genemon,
        text: '"Screen, one, torn. Bar, one, broken. Grain — none lost. Hand, one. Standing."',
      },
      {
        voice: 'narrator',
        text: 'None lost. You think of the gnawed seed-bale at the back wall, and the count you made twice by lantern, and you do not correct him. Not yet.',
      },
      {
        voice: 'steward',
        speaker: NPC_NAME.genemon,
        text: '"The bar was old. It goes against the house, not your wage."',
      },
      {
        voice: 'narrator',
        text: 'Kihei stands, takes the boar-spear off the sill, and puts it back in your hands, butt first.',
      },
      {
        voice: 'arms',
        speaker: NPC_NAME.kihei,
        text: '"You didn\'t win. The house lost nothing. Keep the watch."',
      },
      {
        voice: 'narrator',
        text: 'By evening the padded coat is folded at the woodshed step, the torn shoulder closed with stitches smaller than the tear deserved. O-Hisa is still three steps from the kitchen when you come round the corner, caught.',
      },
      {
        voice: 'steward',
        speaker: NPC_NAME.ohisa,
        text: '"It tore clean, at least. If the ribs knit as straight — well."',
      },
      { voice: 'narrator', text: 'She is back inside before you find anything to say to that.' },
    ],
    topics: [
      {
        id: 'r3-wolf',
        label: '"Will it come back?"',
        answer: [
          {
            voice: 'arms',
            speaker: NPC_NAME.kihei,
            text: '"It left more blood past the woodlot than a wolf keeps spare. If the winter doesn\'t finish it, it remembers this door. So do you. That makes you even."',
          },
        ],
      },
      {
        id: 'r3-ribs',
        label: '"How long?"',
        answer: [
          {
            voice: 'physician',
            speaker: NPC_NAME.soan,
            text: '"Breathe to the bottom of it. Cracked, not broken. Three weeks strapped, nothing heavier than the lantern, and you come to me before you decide you are healed."',
          },
          {
            voice: 'narrator',
            text: 'He writes a line in his ledger and closes it when you turn your head.',
          },
        ],
      },
      {
        id: 'r3-rice',
        label: '"The rice?"',
        answer: [
          {
            voice: 'arms',
            speaker: NPC_NAME.kihei,
            text: '"Where it was put. Last night\'s, at least. The rest is the board\'s arithmetic, not mine."',
          },
        ],
      },
    ],
    decision: {
      prompt: 'What do you do about the wolf?',
      options: [
        {
          id: 'r3-track',
          label: '"It bled going west. The trail is fresh."',
          say: '"It bled going west. The trail is fresh."',
          react:
            '"And you can chase it exactly as far as I can throw you. Sit down. It is winter\'s business now, not yours."',
          reactNpc: 'soan',
          memory: [{ npc: 'kihei', warmthDelta: 1, regard: 'spine' }],
          flags: ['r3-track'],
        },
        {
          id: 'r3-hold',
          label: '"It knows this door now. So do I. I\'ll be at the sill."',
          say: '"It knows this door now. So do I. I\'ll be at the sill."',
          react: '"Then mend the bar before dark, and take the long spear this time. Eat first."',
          memory: [{ npc: 'kihei', warmthDelta: 1, regard: 'steady' }],
          flags: ['r3-hold'],
        },
        {
          id: 'r3-mend',
          label: '"The bar first. Then the wolf."',
          say: '"The bar first. Then the wolf."',
          react: '"The bar, the screen, then the tally. I will enter the wood against the house."',
          reactNpc: 'genemon',
          memory: [{ npc: 'genemon', warmthDelta: 1, regard: 'careful' }],
          flags: ['r3-mend'],
        },
      ],
    },
  },

  R4: {
    id: 'rung-r4',
    rank: 'R4',
    voice: 'arms',
    speaker: 'kihei',
    motivates: [
      'readout-durability',
      'panel-equipment',
      'verb-repair',
      'house-omoya',
      'room-drill-yard',
      'tab-inventory',
    ],
    greeting: [
      {
        voice: 'narrator',
        text: 'Sōan strapped the ribs for walking. Not for work. It is thirty paces from the sickroom door to the board. You count them.',
      },
      {
        voice: 'narrator',
        text: 'Past the kitchen passage a screen stands open on a corridor you have never been sent down. An altar set into the passage wall; rites laid out for somebody; a pair of straw sandals, new this year, facing away from the house. You look a breath too long. The screen slides shut from inside. Nobody says anything to you about it, then or after.',
      },
      {
        voice: 'narrator',
        text: "Genemon is there, the day-book open, the morning's entries not yet dry. He looks up, and waits, and does not help you begin.",
      },
      {
        voice: 'player',
        text: '"Rats were in the store. Three nights, while I lay. A quarter-sack gone. Two more opened at the seam."',
      },
      { voice: 'player', text: '"The watch was mine."' },
      {
        voice: 'narrator',
        text: 'Genemon writes before he answers. It is what he does with surprise, though nothing in his face has admitted to any.',
      },
      {
        voice: 'steward',
        speaker: NPC_NAME.genemon,
        text: '"Rice, one quarter-sack, to vermin. Two sacks to resew — O-Hisa has thread. Entered under the store."',
      },
      {
        voice: 'steward',
        speaker: NPC_NAME.genemon,
        text: '"Not under your name. No wage ran for the nights you lay. No watch stood — the gate cannot leave the gate. The account is even."',
      },
      { voice: 'player', text: '"It isn\'t."' },
      {
        voice: 'narrator',
        text: 'The pen stops above the line a moment longer than the line needs. Then it moves on, and he says nothing, and writes nothing further.',
      },
      {
        voice: 'narrator',
        text: 'At the kitchen threshold O-Hisa has stopped with a bowl in her hands, as if there were something to say to somebody. She sets it down where you will pass, and goes in.',
      },
      {
        voice: 'narrator',
        text: 'From the board, the woodshed is one way and the old stable court is the other. Your ribs are for the woodshed. You go the other way.',
      },
      {
        voice: 'narrator',
        text: 'Stalls for twenty horses run down the long side of the court, and the house owns one mule. Kihei is at the rack, running a whetstone down a spear-head that has no war to go to. Two practice staves lean beside him. One is worn grey at the grip. The other is oiled, and looks as if it has never been anything else.',
      },
      { voice: 'player', text: '"Teach me."' },
      {
        voice: 'narrator',
        text: 'That was the easy half. The question costs more than the walk did.',
      },
      { voice: 'player', text: '"Will you?"' },
      {
        voice: 'narrator',
        text: 'Kihei sets down the whetstone. He looks at the strapping, at the way you keep your left side back, at the scars that were old before the river. He has looked at them before. He has never once asked.',
      },
      {
        voice: 'arms',
        speaker: NPC_NAME.kihei,
        text: '"...You walked here on cracked ribs to ask for more of what cracked them."',
      },
      { voice: 'narrator', text: 'It is not a question, so you do not answer it.' },
      {
        voice: 'arms',
        speaker: NPC_NAME.kihei,
        text: '"Terms, then. Once. Talent is a story the lucky tell. You are not lucky. So you will work."',
      },
      {
        voice: 'narrator',
        text: 'He takes the oiled staff from the rack and holds it out. He does not say how long it has stood there, and you do not ask.',
      },
      {
        voice: 'arms',
        speaker: NPC_NAME.kihei,
        text: '"The house has one man who can stand a watch, and he is grey. Dawn, before the others. Every day the ribs allow, and some they don\'t."',
      },
      {
        voice: 'narrator',
        text: 'On the wall above the empty stalls a boy sits very still, the way boys sit when they believe stillness is the same as not being there.',
      },
      { voice: 'arms', speaker: NPC_NAME.kihei, text: '"One thing before the staff. Why."' },
    ],
    topics: [
      {
        id: 'r4-what-first',
        label: '"What will you teach me?"',
        answer: [
          {
            voice: 'arms',
            speaker: NPC_NAME.kihei,
            text: '"Feet first. Then the staff. The spear when the staff says so. Not the sword — the sword is not yours to carry, and that is the law\'s affair, not a judgment."',
          },
        ],
      },
      {
        id: 'r4-whose-staff',
        label: '"Whose staff is this?"',
        answer: [
          {
            voice: 'arms',
            speaker: NPC_NAME.kihei,
            text: '"One that was kept oiled. Oil is cheap. Ask again when you can hold it right."',
          },
        ],
      },
      {
        id: 'r4-wolf',
        label: '"Will the wolf come back?"',
        answer: [
          {
            voice: 'arms',
            speaker: NPC_NAME.kihei,
            text: '"It went off bleeding into a lean winter. It comes back, or it dies out there. Train for the one that comes back."',
          },
        ],
      },
    ],
    decision: {
      prompt: 'Why do you ask for this?',
      options: [
        {
          id: 'r4-it-returns',
          label: '"It will come back."',
          say: '"It will come back."',
          react: '"It will. Or its like. Dawn, then."',
          memory: [{ npc: 'kihei', warmthDelta: 1, regard: 'watchful' }],
          flags: ['r4-it-returns'],
        },
        {
          id: 'r4-the-rice',
          label: '"I lost the house rice."',
          say: '"I lost the house rice."',
          react:
            '"The rice is Genemon\'s grief. Mine is the sill. Then carry it to this yard at dawn."',
          memory: [{ npc: 'genemon', warmthDelta: 1, regard: 'owned-the-loss' }],
          flags: ['r4-the-rice'],
        },
        {
          id: 'r4-not-carried',
          label: '"I was carried once. Not again."',
          say: '"I was carried once. Not again."',
          react: '"Then don\'t be. Dawn."',
          flags: ['r4-not-carried'],
        },
      ],
    },
  },

  R6: {
    id: 'rung-r6',
    rank: 'R6',
    voice: 'steward',
    speaker: 'genemon',
    motivates: ['house-workshops', 'house-granary', 'tab-estate', 'panel-house-influence'],
    greeting: [
      {
        voice: 'narrator',
        text: "Morning, the forecourt. Genemon's window stands open, the day-book out, and beside it a purse the size of a fist, dark with handling.",
      },
      {
        voice: 'steward',
        speaker: NPC_NAME.genemon,
        text: '"Salt, one shō. Waxed thread, two hanks. Lamp oil, one masu. Yohei sets his boards up at the gate by mid-morning."',
      },
      {
        voice: 'steward',
        speaker: NPC_NAME.genemon,
        text: '"Two hands short since spring, and I cannot leave the book on a market morning. So."',
      },
      {
        voice: 'narrator',
        text: 'He counts the coins into your palm one at a time, watching each one land.',
      },
      { voice: 'steward', speaker: NPC_NAME.genemon, text: '"Forty mon. Count them back."' },
      { voice: 'player', text: '"Forty."' },
      {
        voice: 'narrator',
        text: 'He watches the whole count. When it comes out even he does not nod. He enters something in the day-book, unasked.',
      },
      {
        voice: 'steward',
        speaker: NPC_NAME.genemon,
        text: '"His prices are his own. The house\'s figures are mine. Go."',
      },
      {
        voice: 'narrator',
        text: "Market morning at the gate. Yohei's boards are up and the gateyard has people in it for once — baskets, a mule, talk. The woman ahead of you pulls her basket an inch closer when you stop beside her. The talk is rain over the pass and the price of salt, and it thins a little when you step to the boards, and it is not about you either.",
      },
      {
        voice: 'villager',
        speaker: NPC_NAME.yohei,
        text: "\"The house's hand, and carrying the house's purse — there's news by itself. Rain over the pass, the dye-man's held at the ford a week, so waxed thread is scarce, so the thread is eleven. Salt fifteen, the oil fourteen — fair as water, those two. Eleven for the thread, then?\"",
      },
    ],
    topics: [
      {
        id: 'r6-stray-price',
        label: '"You priced me high, my first market."',
        answer: [
          {
            voice: 'villager',
            speaker: NPC_NAME.yohei,
            text: '"I did. First market, you were the road\'s — a road man pays the road\'s price, carries it off, and is gone. A house man comes back twelve markets a year. I price the coming back."',
          },
        ],
      },
      {
        id: 'r6-ford-news',
        label: '"What holds the dye-man?"',
        answer: [
          {
            voice: 'villager',
            speaker: NPC_NAME.yohei,
            text: '"The ford, swollen, and no hurry in him — lowland wages are fat this year and half the carriers went down for them. Your house lost two hands to the same road, I hear."',
          },
        ],
      },
    ],
    decision: {
      prompt: 'The thread is eleven. The steward reckoned nine.',
      options: [
        {
          id: 'r6-count-flat',
          label: '"Nine. Count it with me."',
          say: '"Nine. Count it with me."',
          react:
            "\"Ha — the steward's figure, out of the steward's own mouth. Nine, nine, take it. Twelve markets a year, I'd rather be the stall you count at than the one you walk past.\"",
          reactNpc: 'yohei',
          memory: [{ npc: 'yohei', warmthDelta: 1, regard: 'square' }],
          flags: ['r6-flat'],
        },
        {
          id: 'r6-pay-over',
          label: '"Eleven, then."',
          say: '"Eleven, then."',
          react:
            '"Done, and kindly done. Salt, oil, thread — mind the oil, it sweats in the sun. My regards to the kitchen."',
          reactNpc: 'yohei',
          memory: [{ npc: 'yohei', warmthDelta: 0, regard: 'a mark still' }],
          flags: ['r6-over'],
        },
        {
          id: 'r6-put-back',
          label: '"Then the house does without thread."',
          say: '"Then the house does without thread."',
          react:
            '"As you like. Thread keeps, and so do I. Salt and oil, twenty-nine — and tell the steward it\'s the dye-man sets that price, not me."',
          reactNpc: 'yohei',
          memory: [{ npc: 'yohei', warmthDelta: 0, regard: 'hard' }],
          flags: ['r6-back'],
        },
      ],
    },
  },

  R7: {
    id: 'rung-r7',
    rank: 'R7',
    voice: 'steward',
    speaker: 'genemon',
    motivates: ['house-study', 'room-grove'],
    greeting: [
      {
        voice: 'narrator',
        text: "The board, past supper, past the year's tax. The household's noises are done for the day. Genemon has the day-book open under the lamp and a fresh page ruled, and he does not look up until the last column of the old one is footed.",
      },
      {
        voice: 'steward',
        speaker: NPC_NAME.genemon,
        text: '"Stand where I can see you. This is quick."',
      },
      {
        voice: 'steward',
        speaker: NPC_NAME.genemon,
        text: '"A full year and more, this book has said \'one man, name unknown.\' Every entry, the same six strokes spent on a man the whole yard knows on sight. Paper is not free. Neither is my evening."',
      },
      {
        voice: 'narrator',
        text: 'He turns back through the pages — a winter, a wolf, a harvest — to the first entry, and reads it aloud, once. One man, name unknown. Then he dips the brush, and holds it over the page a breath longer than any entry needs.',
      },
      {
        voice: 'steward',
        speaker: NPC_NAME.genemon,
        text: '"Gonbei. Every hired man this house has kept has carried it before you. The house\'s name for you, then. Earn a better."',
      },
      {
        voice: 'narrator',
        text: 'He writes it small and square — the hand of a man who has written all his life and never once for pleasure — and the thing is done. One man, Gonbei, sound.',
      },
    ],
    topics: [
      {
        id: 'r7-what-changes',
        label: '"What changes?"',
        answer: [
          {
            voice: 'steward',
            speaker: NPC_NAME.genemon,
            text: '"Your name, on the page. A day-wage, as before, and the house\'s purse when it\'s sent out with you. And watched — as every name on this book is watched."',
          },
          {
            voice: 'steward',
            speaker: NPC_NAME.genemon,
            text: "\"And one thing more. From this season's close I rule a second reckoning — not the hands. The house. Stores past the winter's need, walls that held, ground taken back. One line, each season, in this book's plain words. I have written this house's losses for thirty-one years. I mean to write something else before I am done.\"",
          },
        ],
      },
      {
        id: 'r7-before',
        label: '"Who carried it before me?"',
        answer: [
          {
            voice: 'narrator',
            text: 'The question costs him something to ask, and Genemon looks up as if a chair had spoken. Then he turns the book back further than he turned it for the ink, to a page the lamp barely reaches.',
          },
          {
            voice: 'steward',
            speaker: NPC_NAME.genemon,
            text: '"Four, in my years. Two served their seasons out and went to the lowlands. One died of a winter chest, in the house\'s service; his dates are here. The fourth was let go. There was a year this house could keep no one it did not have to keep. That is the whole of it."',
          },
          {
            voice: 'narrator',
            text: 'The same name, four times over, each line ruled through — dated, footed, closed. He answers the rest of the question by writing the date on the fifth, and the fifth stands open.',
          },
        ],
      },
    ],
    decision: {
      prompt: 'The ink is drying. How do you take the name?',
      options: [
        {
          id: 'r7-plain',
          label: '"It will do."',
          say: '"It will do."',
          react:
            '"So it will. Take what is left in the pot as you pass the kitchen. It is paid for."',
          memory: [{ npc: 'genemon', warmthDelta: 1, regard: 'plain' }],
          flags: ['r7-plain'],
        },
        {
          id: 'r7-earn',
          label: '"Then I\'ll earn the better one."',
          say: '"Then I\'ll earn the better one."',
          react:
            '"Better is earned at the season\'s close, like everything on this book. I will hold you to the word."',
          memory: [{ npc: 'genemon', warmthDelta: 1, regard: 'ambitious' }],
          flags: ['r7-earn'],
        },
        {
          id: 'r7-mine',
          label: '"Until mine comes back, it can serve."',
          say: '"Until mine comes back, it can serve."',
          react: '"As you say. An entry can be amended."',
          memory: [{ npc: 'genemon', warmthDelta: 1, regard: 'guarded' }],
          flags: ['r7-mine'],
        },
      ],
    },
  },
};
